import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit'
import { captureException, captureMessage } from '@tucan/utils/error-tracking'
import { UploadFile } from 'antd'
import axios from 'axios'
import debug from 'debug'
import {
  recordMultipartUploadMutate,
  RecordUploadPartInput,
} from 'graphql/mutation/record.multipart.upload.mutation'
import { recordUploadMutate } from 'graphql/mutation/record.upload.mutation'
import { ID } from 'utils/id'
import { isAudio } from 'utils/is.audio'
import { AppDispatch } from '../index'
import { MessageInstance } from 'antd/es/message/interface'

const logger = debug('store/upload')

const CancelToken = axios.CancelToken
const cancel: { [key: ID]: (message?: string) => void } = {}

const MULTIPART_THRESHOLD = 50 * 1024 * 1024
const MULTIPART_PART_SIZE = 10 * 1024 * 1024
const MULTIPART_CONCURRENCY = 4
const MULTIPART_MAX_ATTEMPTS = 3

const pMap = async <T, R>(
  items: T[],
  fn: (item: T, index: number) => Promise<R>,
  concurrency: number,
): Promise<R[]> => {
  const results: R[] = new Array(items.length)
  let next = 0
  const worker = async () => {
    while (next < items.length) {
      const i = next++
      results[i] = await fn(items[i], i)
    }
  }
  await Promise.all(
    Array.from({ length: Math.min(concurrency, items.length) }, worker),
  )
  return results
}

const uploadRecordMultipart = async (params: {
  file: File
  recordId: ID
  onProgress: (progress: number) => void
}): Promise<void> => {
  const { file, recordId, onProgress } = params
  const chunkCount = Math.ceil(file.size / MULTIPART_PART_SIZE)

  const {
    data: initData,
  } = await recordMultipartUploadMutate({
    variables: {
      chunkCount,
      finished: false,
      id: recordId,
    },
  })

  const uploadId = initData?.upload.uploadId
  const uploadPaths = initData?.upload.uploadPaths
  if (!uploadId || !uploadPaths || uploadPaths.length !== chunkCount) {
    throw new Error('multipart upload init returned unexpected response')
  }

  const source = CancelToken.source()
  cancel[recordId] = source.cancel

  const loaded = new Array<number>(chunkCount).fill(0)

  const parts = await pMap(
    uploadPaths,
    async (url, idx): Promise<RecordUploadPartInput> => {
      const start = idx * MULTIPART_PART_SIZE
      const end = Math.min(start + MULTIPART_PART_SIZE, file.size)
      const blob = file.slice(start, end)

      let lastError: unknown
      for (let attempt = 1; attempt <= MULTIPART_MAX_ATTEMPTS; attempt++) {
        try {
          loaded[idx] = 0
          const res = await axios.put(url, blob, {
            cancelToken: source.token,
            onUploadProgress: (progressEvent) => {
              loaded[idx] = progressEvent.loaded ?? 0
              const total = loaded.reduce((a, b) => a + b, 0)
              onProgress(file.size === 0 ? 0 : total / file.size)
            },
          })

          const rawEtag = res.headers.etag ?? res.headers.ETag
          const etag = typeof rawEtag === 'string'
            ? rawEtag.replace(/^"|"$/g, '')
            : undefined
          if (!etag) {
            throw new Error(
              `missing ETag header for part ${idx + 1} — S3 bucket CORS must include ETag in ExposeHeaders`,
            )
          }

          loaded[idx] = end - start
          const total = loaded.reduce((a, b) => a + b, 0)
          onProgress(file.size === 0 ? 0 : total / file.size)

          return { etag, partNumber: idx + 1 }
        } catch (e) {
          if (axios.isCancel(e)) {
            throw e
          }
          lastError = e
          logger(
            'part %d attempt %d/%d failed: %O',
            idx + 1,
            attempt,
            MULTIPART_MAX_ATTEMPTS,
            e,
          )
        }
      }
      throw lastError
    },
    MULTIPART_CONCURRENCY,
  )

  await recordMultipartUploadMutate({
    variables: {
      finished: true,
      id: recordId,
      parts,
      uploadId,
    },
  })
}

export const uploadTypeValues = [
  'record',
] as const

export type UploadType = typeof uploadTypeValues[number]

export type Upload = {
  id: ID
  name: string
  progress: number
  finished: boolean
  failed: boolean
  instanceId?: ID
  meetingRecordId?: ID
  type?: UploadType
}

const uploadAdapter = createEntityAdapter<Upload>({
  selectId: (upload) => upload.id,
})

export const startRecordUpload = createAsyncThunk<void, {
  message: MessageInstance
  recordId: ID
  name: string
  file: Omit<UploadFile, 'uid'>
  instanceId?: ID
  meetingRecordId?: ID
  type?: UploadType
  extraExtensions?: string[]
}, { dispatch: AppDispatch }>(
  'uploads/startRecordUpload',
  async (data, thunkAPI) => {
    const {
      message,
      recordId,
      name,
      file,
      instanceId,
      meetingRecordId,
      type,
      extraExtensions,
    } = data

    if (!isAudio(file, extraExtensions)) {
      void message.error('Wrong file format')
      return
    }

    thunkAPI.dispatch(addUpload({
      failed: false,
      finished: false,
      id: recordId,
      instanceId,
      meetingRecordId,
      name,
      progress: 0,
      type,
    }))

    const fileSize = (file as Partial<File>).size ?? 0
    const multipart = fileSize > MULTIPART_THRESHOLD

    try {
      if (multipart) {
        await uploadRecordMultipart({
          file: file as unknown as File,
          onProgress: (progress) => {
            thunkAPI.dispatch(updateUpload({
              id: recordId,
              progress,
            }))
          },
          recordId,
        })
      } else {
        const { data: uploadData } = await recordUploadMutate({
          variables: {
            finished: false,
            id: recordId,
          },
        })

        const signedUrl = uploadData?.upload.uri
        if (!signedUrl) {
          throw new Error('upload init returned unexpected response')
        }

        await axios.put(signedUrl, file, {
          cancelToken: new CancelToken(function executor(c) {
            cancel[recordId] = c
          }),
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            thunkAPI.dispatch(updateUpload({
              id: recordId,
              progress: progressEvent.total !== undefined
                ? progressEvent.loaded / progressEvent.total
                : 0,
            }))
          },
        })
      }

      thunkAPI.dispatch(finishUpload(recordId))
    } catch (e) {
      logger('Failed with error: %O', e)
      if (e instanceof Error && e.message === 'Upload canceled') {
        thunkAPI.dispatch(removeUpload(recordId))
      } else {
        captureException(e)
        thunkAPI.dispatch(failUpload(recordId))
      }

      return
    }

    if (multipart) {
      return
    }

    await recordUploadMutate({
      variables: {
        finished: true,
        id: recordId,
      },
    }).catch((e) => {
      captureMessage('finished upload but failed to confirm in api')
      captureException(e)
      logger('finished upload but failed to confirm in api')
    })
  },
)

export const cancelUpload = createAsyncThunk<void, ID>(
  'uploads/cancelUpload',
  (id) => {
    if (cancel[id]) {
      cancel[id]('Upload canceled')
    }
  },
)

export type Uploads = {[key: ID]: Upload}

export const uploadSlice = createSlice({
  initialState: uploadAdapter.getInitialState(),
  name: 'upload',
  reducers: {
    addUpload: uploadAdapter.addOne,
    failUpload: (state, action: PayloadAction<ID>) => {
      const upload = state.entities[action.payload]
      if (upload) {
        upload.failed = true
      }

      return state
    },
    finishUpload: (state, action: PayloadAction<ID>) => {
      const upload = state.entities[action.payload]
      if (upload) {
        upload.finished = true
      }

      return state
    },
    removeUpload: uploadAdapter.removeOne,
    updateUpload: (state, action: PayloadAction<{ id: ID; progress: number }>) => {
      const upload = state.entities[action.payload.id]
      if (upload) {
        upload.progress = action.payload.progress
      }

      return state
    },
  },
})

export const {
  addUpload,
  removeUpload,
  failUpload,
  finishUpload,
  updateUpload,
} = uploadSlice.actions

